// A2-467 (2026-10-21) — which exchange rates a calculation needs, in ONE
// place. PeriodFxRates and MonthlyFxRates each worked this out inside the
// component; the page now also has to say "nothing to convert" when both
// lists are empty, and a third copy of the rule could drift from the two
// boxes it describes. Pure functions, no imports (see flaggedFx.js for why
// a UI import here breaks the unit tests).

/** "YYYY-MM" for every month from start to end inclusive (max 37). */
export const monthsBetween = (start, end) => {
  const out = [];
  if (!start || !end) return out;
  let [y, m] = start.slice(0, 7).split("-").map(Number);
  const [ey, em] = end.slice(0, 7).split("-").map(Number);
  while (y < ey || (y === ey && m <= em)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
    if (out.length > 36) break;
  }
  return out;
};

// National filing currency per registration country. Single source of
// truth mirrors backend engine.filing_currency: GB→GBP; the six non-euro
// EU states file locally; every euro-area state (and the default) → EUR.
// Bulgaria is EUR from 2026-01-01 (Council Regulation (EU) 2025/1409; A2-234).
const _LOCAL_FILING = {
  GB: "GBP", CZ: "CZK", DK: "DKK", HU: "HUF",
  PL: "PLN", RO: "RON", SE: "SEK",
};
export const filingCurrencyFor = (cc) => _LOCAL_FILING[(cc || "").toUpperCase()] || "EUR";

/** Source currencies needing ONE end-of-period rate into `base` (OSS / UK
 *  aggregate box): each selected upload's declared currency, plus the
 *  currencies the review queue already found, except `base` itself. */
export const periodFxSources = (selectedRuns, base, extraCurrencies = []) => {
  const set = new Set();
  for (const r of selectedRuns || []) {
    const c = (r?.declared_currency || "").toUpperCase();
    if (c && c !== base) set.add(c);
  }
  for (const c of extraCurrencies || []) {
    const up = (c || "").toUpperCase();
    if (up && up !== base) set.add(up);
  }
  return Array.from(set).sort();
};

/** Every [source, target] pair needing a MONTHLY national rate: each row
 *  currency against each filing currency of the client's registrations,
 *  unioned with the pairs the review queue already determined. */
export const monthlyFxPairs = (selectedRuns, registrations, extraPairs = []) => {
  const filing = new Set((registrations || []).map(filingCurrencyFor));
  if (filing.size === 0) filing.add("EUR");
  const sources = new Set();
  for (const r of selectedRuns || []) {
    const list = (r?.currencies && r.currencies.length)
      ? r.currencies
      : (r?.declared_currency ? [r.declared_currency] : []);
    for (const c of list) {
      const up = (c || "").toUpperCase();
      if (up) sources.add(up);
    }
  }
  const seen = new Set();
  const out = [];
  const add = (src, tgt) => {
    const key = `${src}|${tgt}`;
    if (src === tgt || seen.has(key)) return;
    seen.add(key);
    out.push([src, tgt]);
  };
  for (const src of [...sources].sort())
    for (const tgt of [...filing].sort())
      add(src, tgt);
  for (const [src, tgt] of extraPairs || []) {
    add((src || "").toUpperCase(), (tgt || "").toUpperCase());
  }
  return out;
};

// A2-474 — only the monthly cells this calculation can use: a pair the
// selected files and registrations need, in a month of the period (a frozen
// ECB day rate, "YYYY-MM-DD", counts for its month). A cell left over from a
// file that is no longer ticked is kept in the editor but neither blocks Run
// nor goes to the server, where an invalid one was refused.
export const reachableMonthlyFx = (monthly, pairs, months) => {
  const want = new Set((pairs || []).map(([s, t]) => `${s}|${t}`));
  const inPeriod = new Set(months || []);
  const out = {};
  for (const [src, byTgt] of Object.entries(monthly || {})) {
    for (const [tgt, byMonth] of Object.entries(byTgt || {})) {
      if (!want.has(`${src.toUpperCase()}|${tgt.toUpperCase()}`)) continue;
      for (const [key, cell] of Object.entries(byMonth || {})) {
        if (!inPeriod.has(String(key).slice(0, 7))) continue;
        ((out[src] = out[src] || {})[tgt] = out[src][tgt] || {})[key] = cell;
      }
    }
  }
  return out;
};
